import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { handleChunkLoadError } from '@/utils/errors.ts';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics.ts';

const oneLine = (value?: string | null) => value?.replace(/\n/g, ' || ') || undefined;

type LogClientErrorProps = {
  codeSourceName: string;
  error: Error;
  details?: (string | undefined)[];
};

const logClientError = ({ codeSourceName, error, details = [] }: LogClientErrorProps) => {
  // A chunk error the guard refused to reload for means the last reload did not help, so it stays an ERROR
  const isReloading = handleChunkLoadError(error);

  clientAppLog({
    level: isReloading ? 'WARN' : 'ERROR',
    codeSourceName,
    logMessage: [codeSourceName, `error:${error?.name}`, `message:${error?.message}`, ...details]
      .filter(Boolean)
      .join(' | '),
    url: window?.location?.href,
  });
};

export const logFromErrorBoundary = (
  error: Error,
  id: string,
  stackTraceEnabled: boolean,
  componentStack?: string | null
) => {
  logClientError({
    codeSourceName: `ErrorBoundary:${id}`,
    error,
    details: stackTraceEnabled
      ? [
          `stackTrace:${oneLine(error?.stack) || 'No stack trace'}`,
          `componentStack:${oneLine(componentStack) || 'No component stack'}`,
        ]
      : [],
  });

  sendMetricIncrement(`app.js-error.error-boundary.${id}.triggered`);
};

export const countUncaughtError = (id: string) => sendMetricIncrement(`app.js-error.uncaught.${id}.triggered`);

// No id and no component stack to localise these, so the stack is the only locator: always send it.
export const logUncaughtError = (id: string, error: Error, source?: string) => {
  logClientError({
    codeSourceName: `Uncaught:${id}`,
    error,
    details: [source, `stackTrace:${oneLine(error?.stack) || 'No stack trace'}`],
  });

  countUncaughtError(id);
};
